import { useEffect, useRef, useState } from "react";
import { getExcelData, getSite } from "../../../../utils/api";

const useSpinachDashboardAutoRefresh = ({
  messageApi,
  siteMetadata,
  selectedGreenhouses,
  selectedGreenhousesSet,
  greenhouseData,
  fetchGreenhouseData,
  currentOrg,
  currentSite,
  currentCrop,
  setSiteMetadata,
  setAllPlotDataLoading,
  setExcelRawData,
  buildSummaryForecast,
  allRowGraphData,
  searchParams,
  setSearchParams,
}) => {
  const [autoRefreshSec, setAutoRefreshSec] = useState(0);
  const refreshIntervalIdRef = useRef(null);
  const [greenhouseMapVisible, setGreenhouseMapVisible] = useState(false);

  // -----------------------------------
  // Silent Refresh on demand
  // -----------------------------------
  const silentRefresh = async () => {
    try {
      // Show "Loading" in a pinned message:
      messageApi.open({
        key: "autorefresh-countdown",
        type: "loading",
        content: "Fetching data, please wait...",
        duration: 0,
      });

      if (!selectedGreenhouses?.length) {
        messageApi.open({
          key: "autorefresh-countdown",
          type: "success",
          content: "No Data to update!",
        });
        return;
      }
      const greenhouseIds = greenhouseData
        .filter((item) => selectedGreenhousesSet.has(item.name))
        .map((item) => item.id);

      // 1) Re‐fetch greenhouse plot data
      await fetchGreenhouseData(greenhouseIds);

      // 2) Re‐fetch Excel data
      const greenhouseNames = greenhouseData.map((g) => g.name);
      const newExcelRes = await getExcelData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        null,
        greenhouseNames,
      );

      messageApi.open({
        key: "autorefresh-countdown",
        type: "loading",
        content: "Data is updating, please wait...",
        duration: 0,
      });

      setAllPlotDataLoading(true);

      const newExcel = newExcelRes?.data?.data?.rows;
      setExcelRawData(newExcel);

      // 3) Re‐build summary
      await buildSummaryForecast(newExcel, allRowGraphData);

      // 4) Success message
      messageApi.open({
        key: "autorefresh-countdown",
        type: "success",
        content: "Data updated!",
      });

      // 5) Fetch updated site => update site & siteMetadata
      const updatedSiteRes = await getSite(currentSite.id);
      const updatedSite = updatedSiteRes?.data?.data;
      if (updatedSite) {
        setSiteMetadata(updatedSite.metadata?.[currentCrop.name] || {});
      }
    } catch (err) {
      console.error("Silent refresh error:", err);
      messageApi.open({
        key: "autorefresh-countdown",
        type: "error",
        content: "Error refreshing data.",
      });
    } finally {
      setAllPlotDataLoading(false);
    }
  };

  // -----------------------------------
  // Setup / reset auto refresh time
  // -----------------------------------
  // Whenever FORECAST_PAGE_REFRESH_TIME_IN_MIN changes, reset the countdown
  useEffect(() => {
    // clear any existing timer
    if (refreshIntervalIdRef.current) {
      clearInterval(refreshIntervalIdRef.current);
      refreshIntervalIdRef.current = null;
    }
    const refreshMin = siteMetadata?.FORECAST_PAGE_REFRESH_TIME_IN_MIN || 0;
    if (!refreshMin) {
      setAutoRefreshSec(0);
      return;
    }
    const newSec = refreshMin * 60;
    setAutoRefreshSec(newSec);
  }, [siteMetadata]);

  // The actual countdown effect
  useEffect(() => {
    if (!autoRefreshSec) return;
    const forecastPageRefreshCountdown =
      siteMetadata?.FORECAST_PAGE_REFRESH_COUNTDOWN_IN_SEC
        ? siteMetadata?.FORECAST_PAGE_REFRESH_COUNTDOWN_IN_SEC + 1
        : 11;
    refreshIntervalIdRef.current = setInterval(() => {
      setAutoRefreshSec((prev) => {
        if (prev <= 1) {
          messageApi.open({
            key: "autorefresh-countdown",
            type: "loading",
            content: "Fetching data, please wait...",
            duration: 0,
          });
          silentRefresh();
          // reset countdown
          const refreshMinAgain =
            siteMetadata?.FORECAST_PAGE_REFRESH_TIME_IN_MIN || 0;
          return refreshMinAgain * 60;
        }

        if (prev <= forecastPageRefreshCountdown) {
          messageApi.open({
            key: "autorefresh-countdown",
            type: "info",
            content: `Data will refresh in ${prev - 1} seconds...`,
            duration: 0,
          });
        }
        return prev - 1;
      });
    }, 1000);

    // cleanup
    return () => {
      if (refreshIntervalIdRef.current) {
        clearInterval(refreshIntervalIdRef.current);
        refreshIntervalIdRef.current = null;
      }
    };
  }, [autoRefreshSec]);

  useEffect(() => {
    const paramVal = searchParams.get("showMap");
    if (paramVal === "true" || paramVal === "1") {
      setGreenhouseMapVisible(true);
    }
  }, []);

  const openMapModal = () => {
    setGreenhouseMapVisible(true);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("showMap", "true");
    setSearchParams(newParams);
  };

  const closeMapModal = () => {
    setGreenhouseMapVisible(false);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("showMap");
    setSearchParams(newParams);
  };

  return {
    greenhouseMapVisible,
    openMapModal,
    closeMapModal,
  };
};

export default useSpinachDashboardAutoRefresh;
